/**
 * Admin Page
 * 
 * Administration dashboard for superadmin and client_admin users.
 * Manages users, API integrations, and system settings.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Settings, Link2, Shield, Plus, Trash2, Edit,
    ToggleLeft, ToggleRight, RefreshCw, Key, Activity,
    Check, X, AlertTriangle, Search, ChevronDown,
    Database, Download, Upload, Globe, Zap, Loader2,
    ExternalLink, Calendar, MapPin, Plane
} from 'lucide-react';
import api from '../services/api';
import { aerodromeService, type AerodromeData, type AerodromeSummary, type AerodromeStats, type AIPSource, type BatchSeedResult } from '../services/aerodromeService';
import { useAuth, UserRole } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

// Types
interface User {
    id: string;
    email: string;
    full_name: string;
    role: string;
    is_active: boolean;
    organization_id?: string;
}

interface Integration {
    id: string;
    name: string;
    description: string;
    base_url: string;
    is_active: boolean;
    auth_type: string;
    last_sync?: string;
}

interface UserStats {
    total_users: number;
    active_users: number;
    inactive_users: number;
    by_role: Record<string, number>;
}

type TabType = 'users' | 'integrations' | 'settings' | 'audit' | 'aip_agent';

const TEXTS = {
    es: {
        title: 'Administración',
        subtitle: 'Gestionar usuarios, integraciones y configuración del sistema',
        users: 'Usuarios', integrations: 'Integraciones', settings: 'Configuración', auditLog: 'Registro de Auditoría',
        totalUsers: 'Total Usuarios', active: 'Activos', inactive: 'Inactivos', admins: 'Administradores',
        searchUsers: 'Buscar usuarios...', addUser: 'Añadir Usuario',
        user: 'Usuario', role: 'Rol', status: 'Estado', actions: 'Acciones',
        loading: 'Cargando...', noUsers: 'No se encontraron usuarios',
        activeStatus: 'Activo', inactiveStatus: 'Inactivo',
        deactivate: 'Desactivar', activate: 'Activar',
        resetPassword: 'Restablecer Contraseña', editUser: 'Editar', deleteUser: 'Eliminar',
        baseUrl: 'URL Base', authType: 'Tipo Auth', lastSync: 'Ultima Sincr.', never: 'Nunca',
        testConnection: 'Probar Conexión',
        generalSettings: 'Configuración General',
        appName: 'Nombre de la Aplicación', companyName: 'Nombre de la Empresa',
        defaultLanguage: 'Idioma por Defecto', sessionTimeout: 'Tiempo de Sesión (minutos)',
        systemStatus: 'Estado del Sistema',
        maintenanceMode: 'Modo Mantenimiento', enableNotifications: 'Habilitar Notificaciones',
        saveChanges: 'Guardar Cambios',
        recentActivity: 'Actividad Reciente',
        createUser: 'Crear Nuevo Usuario', fullName: 'Nombre Completo', email: 'Email',
        activeOnCreation: 'Activo al crear', cancel: 'Cancelar', creating: 'Creando...', create: 'Crear Usuario',
        confirmDelete: '¿Estás seguro de que quieres eliminar este usuario?',
        // AIP Agent
        aipAgent: 'AIP Agent', aipAgentDesc: 'Gestionar base de datos de aeródromos',
        totalAerodromes: 'Total Aeródromos', seeded: 'Seeded (OurAirports)', enriched: 'Enriquecidos (AIP)',
        withStands: 'Con Stands', airacCycle: 'Ciclo AIRAC', nextCycle: 'Próximo Ciclo',
        daysUntil: 'días restantes', searchAerodrome: 'Buscar aeródromo (ICAO, IATA o nombre)...',
        seedSingle: 'Seed Individual', seedBatch: 'Seed Masivo', enrichAerodrome: 'Enriquecer',
        sourceUrls: 'URLs AIP', addUrl: 'Registrar URL', noAerodromes: 'No se encontraron aeródromos',
        dataSource: 'Fuente', confidence: 'Confianza', lastEnriched: 'Último Enriquecimiento',
        runways: 'Pistas', taxiways: 'Calles Rodaje', stands: 'Stands', frequencies: 'Frecuencias',
        selectAerodrome: 'Selecciona un aeródromo para ver sus datos detallados',
        seedingProgress: 'Sembrando datos...', enrichingProgress: 'Enriqueciendo con AIP...',
        seedSuccess: 'Seed completado', enrichSuccess: 'Enriquecimiento completado',
        url: 'URL', sourceType: 'Tipo', portal: 'Portal', fetchStatus: 'Estado',
        registerUrl: 'Registrar nueva URL AIP', instantLoad: '⚡ Carga instantánea desde BD',
    },
    en: {
        title: 'Administration',
        subtitle: 'Manage users, integrations, and system settings',
        users: 'Users', integrations: 'Integrations', settings: 'Settings', auditLog: 'Audit Log',
        totalUsers: 'Total Users', active: 'Active', inactive: 'Inactive', admins: 'Admins',
        searchUsers: 'Search users...', addUser: 'Add User',
        user: 'User', role: 'Role', status: 'Status', actions: 'Actions',
        loading: 'Loading...', noUsers: 'No users found',
        activeStatus: 'Active', inactiveStatus: 'Inactive',
        deactivate: 'Deactivate', activate: 'Activate',
        resetPassword: 'Reset Password', editUser: 'Edit', deleteUser: 'Delete',
        baseUrl: 'Base URL', authType: 'Auth Type', lastSync: 'Last Sync', never: 'Never',
        testConnection: 'Test Connection',
        generalSettings: 'General Settings',
        appName: 'Application Name', companyName: 'Company Name',
        defaultLanguage: 'Default Language', sessionTimeout: 'Session Timeout (minutes)',
        systemStatus: 'System Status',
        maintenanceMode: 'Maintenance Mode', enableNotifications: 'Enable Notifications',
        saveChanges: 'Save Changes',
        recentActivity: 'Recent Activity',
        createUser: 'Create New User', fullName: 'Full Name', email: 'Email',
        activeOnCreation: 'Active on creation', cancel: 'Cancel', creating: 'Creating...', create: 'Create User',
        confirmDelete: 'Are you sure you want to delete this user?',
        // AIP Agent
        aipAgent: 'AIP Agent', aipAgentDesc: 'Manage aerodrome database',
        totalAerodromes: 'Total Aerodromes', seeded: 'Seeded (OurAirports)', enriched: 'Enriched (AIP)',
        withStands: 'With Stands', airacCycle: 'AIRAC Cycle', nextCycle: 'Next Cycle',
        daysUntil: 'days remaining', searchAerodrome: 'Search aerodrome (ICAO, IATA or name)...',
        seedSingle: 'Seed Single', seedBatch: 'Batch Seed', enrichAerodrome: 'Enrich',
        sourceUrls: 'AIP URLs', addUrl: 'Register URL', noAerodromes: 'No aerodromes found',
        dataSource: 'Source', confidence: 'Confidence', lastEnriched: 'Last Enriched',
        runways: 'Runways', taxiways: 'Taxiways', stands: 'Stands', frequencies: 'Frequencies',
        selectAerodrome: 'Select an aerodrome to view its detailed data',
        seedingProgress: 'Seeding data...', enrichingProgress: 'Enriching with AIP...',
        seedSuccess: 'Seed complete', enrichSuccess: 'Enrichment complete',
        url: 'URL', sourceType: 'Type', portal: 'Portal', fetchStatus: 'Status',
        registerUrl: 'Register new AIP URL', instantLoad: '⚡ Instant load from DB',
    },
};

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const { language } = useLanguage();
    const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;

    const [activeTab, setActiveTab] = useState<TabType>('users');
    const [users, setUsers] = useState<User[]>([]);
    const [userStats, setUserStats] = useState<UserStats | null>(null);
    const [integrations, setIntegrations] = useState<Integration[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);

    // AIP Agent state
    const [aipStats, setAipStats] = useState<AerodromeStats | null>(null);
    const [aipSearchTerm, setAipSearchTerm] = useState('');
    const [aipSearchResults, setAipSearchResults] = useState<AerodromeSummary[]>([]);
    const [selectedAerodrome, setSelectedAerodrome] = useState<AerodromeData | null>(null);
    const [aipSources, setAipSources] = useState<AIPSource[]>([]);
    const [aipLoading, setAipLoading] = useState(false);
    const [aipMessage, setAipMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
    const [seedingIcao, setSeedingIcao] = useState('');
    const [batchSeeding, setBatchSeeding] = useState(false);
    const [batchResult, setBatchResult] = useState<BatchSeedResult | null>(null);
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [newUrl, setNewUrl] = useState({ url: '', source_type: 'pdf_url', portal_name: '', notes: '' });

    // Check admin access
    useEffect(() => {
        if (currentUser && currentUser.role !== UserRole.SUPERADMIN && currentUser.role !== UserRole.CLIENT_ADMIN) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    // Load data based on active tab
    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'users') {
                const [usersRes, statsRes] = await Promise.all([
                    api.get('/admin/users'),
                    api.get('/admin/users/stats')
                ]);
                setUsers(usersRes.data);
                setUserStats(statsRes.data);
            } else if (activeTab === 'integrations') {
                const res = await api.get('/admin/integrations');
                setIntegrations(res.data);
            } else if (activeTab === 'aip_agent') {
                const stats = await aerodromeService.getStats();
                setAipStats(stats);
            }
        } catch (error) {
            console.error('Error loading admin data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleUserActive = async (userId: string) => {
        try {
            await api.post(`/admin/users/${userId}/toggle-active`);
            loadData();
        } catch (error) {
            console.error('Error toggling user status:', error);
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!confirm(tx.confirmDelete)) return;
        try {
            await api.delete(`/admin/users/${userId}`);
            loadData();
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const handleResetPassword = async (userId: string) => {
        try {
            const res = await api.post(`/admin/users/${userId}/reset-password`);
            alert(`New temporary password: ${res.data.temp_password}\n\nPlease share this with the user securely.`);
        } catch (error) {
            console.error('Error resetting password:', error);
        }
    };

    const handleTestIntegration = async (integrationId: string) => {
        try {
            const res = await api.post(`/admin/integrations/${integrationId}/test`);
            alert(`Test Result: ${res.data.test_result}\nLatency: ${res.data.latency_ms}ms\n${res.data.message}`);
        } catch (error) {
            console.error('Error testing integration:', error);
        }
    };

    const handleToggleIntegration = async (integrationId: string, currentState: boolean) => {
        try {
            await api.patch(`/admin/integrations/${integrationId}`, {
                is_active: !currentState
            });
            loadData();
        } catch (error) {
            console.error('Error toggling integration:', error);
        }
    };

    const filteredUsers = users.filter(u =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const tabs = [
        { id: 'users', label: tx.users, icon: Users },
        { id: 'integrations', label: tx.integrations, icon: Link2 },
        { id: 'settings', label: tx.settings, icon: Settings },
        { id: 'audit', label: tx.auditLog, icon: Activity },
        { id: 'aip_agent', label: tx.aipAgent, icon: Database },
    ] as const;

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)]">
            {/* Header */}
            <div className="bg-[var(--surface-raised)] border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl">
                            <Shield className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{tx.title}</h1>
                            <p className="text-[var(--text-secondary)]">{tx.subtitle}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-[var(--surface-raised)]/50 border-b border-[var(--border)]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id
                                        ? 'border-[#F26430] text-[var(--text-primary)]'
                                        : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="space-y-6">
                        {/* Stats Cards */}
                        {userStats && (
                            <div className="grid grid-cols-4 gap-4">
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-[var(--text-primary)]">{userStats.total_users}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.totalUsers}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-green-400">{userStats.active_users}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.active}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-[var(--text-tertiary)]">{userStats.inactive_users}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.inactive}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-purple-400">{userStats.by_role?.superadmin || 0}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.admins}</div>
                                </div>
                            </div>
                        )}

                        {/* Search and Add */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
                                <input
                                    type="text"
                                    placeholder={tx.searchUsers}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg pl-10 pr-4 py-2 text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[#F26430] focus:outline-none"
                                />
                            </div>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="flex items-center gap-2 bg-gradient-to-r from-[#F26430] to-orange-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                            >
                                <Plus className="w-4 h-4" />
                                {tx.addUser}
                            </button>
                        </div>

                        {/* Users Table */}
                        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-[var(--surface-raised)]">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-[var(--text-secondary)] text-sm font-medium">{tx.user}</th>
                                        <th className="text-left px-6 py-3 text-[var(--text-secondary)] text-sm font-medium">{tx.role}</th>
                                        <th className="text-left px-6 py-3 text-[var(--text-secondary)] text-sm font-medium">{tx.status}</th>
                                        <th className="text-right px-6 py-3 text-[var(--text-secondary)] text-sm font-medium">{tx.actions}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[var(--border-subtle)]">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-8 text-center text-[var(--text-secondary)]">
                                                {tx.loading}
                                            </td>
                                        </tr>
                                    ) : filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-8 text-center text-[var(--text-secondary)]">
                                                {tx.noUsers}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map(user => (
                                            <tr key={user.id} className="hover:bg-[var(--surface-raised)]">
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <div className="font-medium">{user.full_name}</div>
                                                        <div className="text-sm text-[var(--text-secondary)]">{user.email}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${user.role === 'superadmin' ? 'bg-purple-500/20 text-purple-300' :
                                                            user.role === 'client_admin' ? 'bg-blue-500/20 text-blue-300' :
                                                                'bg-gray-500/20 text-gray-300'
                                                        }`}>
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`flex items-center gap-1 ${user.is_active ? 'text-green-400' : 'text-[var(--text-tertiary)]'}`}>
                                                        {user.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                                        {user.is_active ? tx.activeStatus : tx.inactiveStatus}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleToggleUserActive(user.id)}
                                                            className="p-2 hover:bg-[var(--surface-raised)] rounded-lg transition"
                                                            title={user.is_active ? tx.deactivate : tx.activate}
                                                        >
                                                            {user.is_active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-[var(--text-tertiary)]" />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleResetPassword(user.id)}
                                                            className="p-2 hover:bg-[var(--surface-inset)] rounded-lg transition"
                                                            title={tx.resetPassword}
                                                        >
                                                            <Key className="w-4 h-4 text-yellow-400" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingUser(user)}
                                                            className="p-2 hover:bg-[var(--surface-inset)] rounded-lg transition"
                                                            title={tx.editUser}
                                                        >
                                                            <Edit className="w-4 h-4 text-blue-400" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUser(user.id)}
                                                            className="p-2 hover:bg-[var(--surface-inset)] rounded-lg transition"
                                                            title={tx.deleteUser}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-400" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Integrations Tab */}
                {activeTab === 'integrations' && (
                    <div className="grid grid-cols-2 gap-6">
                        {loading ? (
                            <div className="col-span-2 text-center py-12 text-[var(--text-secondary)]">{tx.loading}</div>
                        ) : (
                            integrations.map(integration => (
                                <div key={integration.id} className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${integration.is_active ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                                                <Link2 className={`w-5 h-5 ${integration.is_active ? 'text-green-400' : 'text-[var(--text-tertiary)]'}`} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{integration.name}</h3>
                                                <p className="text-sm text-[var(--text-secondary)]">{integration.description}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleToggleIntegration(integration.id, integration.is_active)}
                                            className={`p-2 rounded-lg transition ${integration.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-[var(--text-tertiary)]'}`}
                                        >
                                            {integration.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                                        </button>
                                    </div>

                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">{tx.baseUrl}</span>
                                            <span className="font-mono text-xs">{integration.base_url}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">{tx.authType}</span>
                                            <span className="capitalize">{integration.auth_type}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">{tx.lastSync}</span>
                                            <span>{integration.last_sync ? new Date(integration.last_sync).toLocaleString() : tx.never}</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 mt-4">
                                        <button
                                            onClick={() => handleTestIntegration(integration.id)}
                                            className="flex-1 flex items-center justify-center gap-2 bg-[var(--surface-raised)] hover:bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg py-2 transition"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                            {tx.testConnection}
                                        </button>
                                        <button className="flex items-center justify-center gap-2 bg-[var(--surface-raised)] hover:bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 transition">
                                            <Settings className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="max-w-2xl space-y-6">
                        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-6">
                            <h2 className="text-lg font-semibold mb-4">{tx.generalSettings}</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.appName}</label>
                                    <input type="text" defaultValue="AirportOS" className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.companyName}</label>
                                    <input type="text" defaultValue="Aipron" className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.defaultLanguage}</label>
                                    <select className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none">
                                        <option value="en">English</option>
                                        <option value="es">Español</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.sessionTimeout}</label>
                                    <input type="number" defaultValue={60} className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-6">
                            <h2 className="text-lg font-semibold mb-4">{tx.systemStatus}</h2>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span>{tx.maintenanceMode}</span>
                                    <button className="p-2 bg-gray-500/20 text-[var(--text-tertiary)] rounded-lg">
                                        <ToggleLeft className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>{tx.enableNotifications}</span>
                                    <button className="p-2 bg-green-500/20 text-green-400 rounded-lg">
                                        <ToggleRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button className="w-full bg-gradient-to-r from-[#F26430] to-orange-500 text-white py-3 rounded-lg font-medium hover:opacity-90 transition">
                            {tx.saveChanges}
                        </button>
                    </div>
                )}

                {/* Audit Log Tab */}
                {activeTab === 'audit' && (
                    <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)]">
                        <div className="p-4 border-b border-[var(--border)]">
                            <h2 className="font-semibold">{tx.recentActivity}</h2>
                        </div>
                        <div className="divide-y divide-[var(--border-subtle)]">
                            {[
                                { time: '12:00', user: 'admin@aipron.com', action: 'USER_CREATED', details: 'Created user john@example.com' },
                                { time: '11:30', user: 'admin@aipron.com', action: 'INTEGRATION_UPDATED', details: 'Updated OpenWeather API key' },
                                { time: '10:00', user: 'system', action: 'DACAMS_ANALYSIS', details: 'Project analysis completed' },
                                { time: '09:30', user: 'admin@aipron.com', action: 'USER_DEACTIVATED', details: 'Deactivated user test@example.com' },
                                { time: '09:00', user: 'system', action: 'BACKUP_COMPLETED', details: 'Daily backup completed successfully' },
                            ].map((log, i) => (
                                <div key={i} className="flex items-start gap-4 p-4 hover:bg-[var(--surface-raised)]">
                                    <div className="text-[var(--text-tertiary)] text-sm w-16">{log.time}</div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{log.action}</span>
                                            <span className="text-[var(--text-secondary)] text-sm">by {log.user}</span>
                                        </div>
                                        <p className="text-sm text-[var(--text-secondary)]">{log.details}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* AIP Agent Tab */}
                {activeTab === 'aip_agent' && (
                    <div className="space-y-6">
                        {/* Stats Dashboard */}
                        {aipStats && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-[var(--text-primary)]">{aipStats.total_aerodromes}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.totalAerodromes}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-blue-400">{aipStats.seeded_ourairports}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.seeded}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-green-400">{aipStats.enriched_aip}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.enriched}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-3xl font-bold text-purple-400">{aipStats.with_stands}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.withStands}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-2xl font-bold font-mono text-[#F26430]">{aipStats.airac?.current_cycle}</div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.airacCycle}</div>
                                </div>
                                <div className="bg-[var(--surface-raised)] rounded-xl p-4 border border-[var(--border)]">
                                    <div className="text-2xl font-bold text-amber-400">{aipStats.airac?.days_until_next}<span className="text-sm font-normal text-[var(--text-tertiary)]"> d</span></div>
                                    <div className="text-[var(--text-secondary)] text-sm">{tx.nextCycle}</div>
                                </div>
                            </div>
                        )}

                        {/* Search + Seed Controls */}
                        <div className="flex flex-col lg:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
                                    <input
                                        type="text"
                                        placeholder={tx.searchAerodrome}
                                        value={aipSearchTerm}
                                        onChange={(e) => setAipSearchTerm(e.target.value)}
                                        onKeyDown={async (e) => {
                                            if (e.key === 'Enter' && aipSearchTerm.length >= 2) {
                                                setAipLoading(true);
                                                try {
                                                    const results = await aerodromeService.search(aipSearchTerm);
                                                    setAipSearchResults(results);
                                                } catch { setAipSearchResults([]); }
                                                setAipLoading(false);
                                            }
                                        }}
                                        className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg pl-10 pr-4 py-2 text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[#F26430] focus:outline-none"
                                    />
                                </div>
                                <button
                                    onClick={async () => {
                                        if (aipSearchTerm.length >= 2) {
                                            setAipLoading(true);
                                            try {
                                                const results = await aerodromeService.search(aipSearchTerm);
                                                setAipSearchResults(results);
                                            } catch { setAipSearchResults([]); }
                                            setAipLoading(false);
                                        }
                                    }}
                                    className="px-4 py-2 bg-[var(--surface-raised)] border border-[var(--border)] rounded-lg hover:bg-[var(--surface-inset)] transition text-[var(--text-secondary)]"
                                >
                                    <Search className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Seed Controls */}
                            <div className="flex gap-2">
                                <div className="flex gap-1 items-center">
                                    <input
                                        type="text"
                                        placeholder="ICAO"
                                        value={seedingIcao}
                                        onChange={(e) => setSeedingIcao(e.target.value.toUpperCase())}
                                        maxLength={4}
                                        className="w-20 bg-[var(--surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-center font-mono text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[#F26430] focus:outline-none"
                                    />
                                    <button
                                        onClick={async () => {
                                            if (seedingIcao.length !== 4) return;
                                            setAipLoading(true);
                                            setAipMessage({ type: 'info', text: tx.seedingProgress });
                                            try {
                                                const result = await aerodromeService.seedSingle(seedingIcao);
                                                if (result) {
                                                    setSelectedAerodrome(result);
                                                    setAipMessage({ type: 'success', text: `${tx.seedSuccess}: ${result.name}` });
                                                    // Refresh stats
                                                    const stats = await aerodromeService.getStats();
                                                    setAipStats(stats);
                                                } else {
                                                    setAipMessage({ type: 'error', text: `${seedingIcao} not found in OurAirports` });
                                                }
                                            } catch (err: any) {
                                                setAipMessage({ type: 'error', text: err.response?.data?.detail || 'Seed failed' });
                                            }
                                            setAipLoading(false);
                                        }}
                                        disabled={aipLoading || seedingIcao.length !== 4}
                                        className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition disabled:opacity-50 text-sm"
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        {tx.seedSingle}
                                    </button>
                                </div>
                                <button
                                    onClick={async () => {
                                        setBatchSeeding(true);
                                        setBatchResult(null);
                                        setAipMessage({ type: 'info', text: tx.seedingProgress });
                                        try {
                                            const result = await aerodromeService.seedBatch();
                                            setBatchResult(result);
                                            setAipMessage({ type: 'success', text: `${tx.seedSuccess}: ${result.created} created, ${result.updated} updated` });
                                            const stats = await aerodromeService.getStats();
                                            setAipStats(stats);
                                        } catch (err: any) {
                                            setAipMessage({ type: 'error', text: err.response?.data?.detail || 'Batch seed failed' });
                                        }
                                        setBatchSeeding(false);
                                    }}
                                    disabled={batchSeeding || aipLoading}
                                    className="flex items-center gap-1 bg-gradient-to-r from-[#F26430] to-orange-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 text-sm"
                                >
                                    {batchSeeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                                    {tx.seedBatch}
                                </button>
                            </div>
                        </div>

                        {/* Status Message */}
                        {aipMessage && (
                            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                                aipMessage.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                                : aipMessage.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                                : 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                            }`}>
                                {aipMessage.type === 'success' ? <Check className="w-4 h-4" /> :
                                 aipMessage.type === 'error' ? <AlertTriangle className="w-4 h-4" /> :
                                 <Loader2 className="w-4 h-4 animate-spin" />}
                                {aipMessage.text}
                                <button onClick={() => setAipMessage(null)} className="ml-auto">
                                    <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100" />
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Search Results List */}
                            <div className="lg:col-span-1">
                                <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] overflow-hidden">
                                    <div className="p-3 border-b border-[var(--border)] bg-[var(--surface-raised)]">
                                        <h3 className="text-sm font-semibold flex items-center gap-2">
                                            <Database className="w-4 h-4 text-[#F26430]" />
                                            {tx.totalAerodromes}: {aipSearchResults.length}
                                        </h3>
                                    </div>
                                    <div className="max-h-[500px] overflow-y-auto divide-y divide-[var(--border-subtle)]">
                                        {aipSearchResults.length === 0 ? (
                                            <div className="p-6 text-center text-[var(--text-tertiary)] text-sm">
                                                {aipLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.noAerodromes}
                                            </div>
                                        ) : (
                                            aipSearchResults.map((a) => (
                                                <button
                                                    key={a.icao_code}
                                                    onClick={async () => {
                                                        setAipLoading(true);
                                                        try {
                                                            const data = await aerodromeService.get(a.icao_code);
                                                            if (data) {
                                                                setSelectedAerodrome(data);
                                                                const sources = await aerodromeService.listSources(a.icao_code);
                                                                setAipSources(sources);
                                                            }
                                                        } catch {}
                                                        setAipLoading(false);
                                                    }}
                                                    className={`w-full text-left p-3 hover:bg-[var(--surface-raised)] transition ${
                                                        selectedAerodrome?.icao_code === a.icao_code ? 'bg-[var(--surface-raised)] border-l-2 border-l-[#F26430]' : ''
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono font-bold text-sm">{a.icao_code}</span>
                                                        {a.iata_code && <span className="text-[10px] text-[var(--text-tertiary)] bg-[var(--surface-inset)] px-1.5 py-0.5 rounded">{a.iata_code}</span>}
                                                        {a.data_source === 'aip_pdf' && <Zap className="w-3 h-3 text-green-400" />}
                                                    </div>
                                                    <div className="text-xs text-[var(--text-secondary)] mt-0.5 truncate">{a.name}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                            a.data_source === 'aip_pdf' ? 'bg-green-500/20 text-green-400'
                                                            : a.data_source === 'ourairports' ? 'bg-blue-500/20 text-blue-400'
                                                            : 'bg-gray-500/20 text-gray-400'
                                                        }`}>{a.data_source}</span>
                                                        {a.has_runways && <Plane className="w-3 h-3 text-[var(--text-tertiary)]" />}
                                                        {a.has_stands && <MapPin className="w-3 h-3 text-[var(--text-tertiary)]" />}
                                                        <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">{Math.round(a.confidence * 100)}%</span>
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Aerodrome Detail Panel */}
                            <div className="lg:col-span-2">
                                {selectedAerodrome ? (
                                    <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] overflow-hidden">
                                        {/* Header */}
                                        <div className="p-4 border-b border-[var(--border)] bg-[var(--surface-raised)]">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-lg font-bold">{selectedAerodrome.icao_code}</h3>
                                                        {selectedAerodrome.iata_code && <span className="text-xs bg-[var(--surface-inset)] px-2 py-0.5 rounded text-[var(--text-secondary)]">{selectedAerodrome.iata_code}</span>}
                                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                                            selectedAerodrome.data_source === 'aip_pdf' ? 'bg-green-500/20 text-green-400'
                                                            : selectedAerodrome.data_source === 'ourairports' ? 'bg-blue-500/20 text-blue-400'
                                                            : 'bg-gray-500/20 text-gray-400'
                                                        }`}>{selectedAerodrome.data_source}</span>
                                                        {selectedAerodrome.airac_cycle && (
                                                            <span className="text-xs font-mono text-[#F26430]">AIRAC {selectedAerodrome.airac_cycle}</span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-[var(--text-secondary)] mt-0.5">{selectedAerodrome.name}</p>
                                                    <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                                                        {selectedAerodrome.municipality}, {selectedAerodrome.country_code} · {selectedAerodrome.elevation_ft} ft · {selectedAerodrome.confidence ? `${Math.round(selectedAerodrome.confidence * 100)}%` : '—'}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={async () => {
                                                            setAipLoading(true);
                                                            setAipMessage({ type: 'info', text: tx.enrichingProgress });
                                                            try {
                                                                const result = await aerodromeService.enrichFromRegistry(selectedAerodrome.icao_code);
                                                                setSelectedAerodrome(result);
                                                                setAipMessage({ type: 'success', text: tx.enrichSuccess });
                                                                const stats = await aerodromeService.getStats();
                                                                setAipStats(stats);
                                                            } catch (err: any) {
                                                                setAipMessage({ type: 'error', text: err.response?.data?.detail || 'Enrichment failed' });
                                                            }
                                                            setAipLoading(false);
                                                        }}
                                                        disabled={aipLoading}
                                                        className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition text-xs disabled:opacity-50"
                                                    >
                                                        <Zap className="w-3.5 h-3.5" />
                                                        {tx.enrichAerodrome}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowUrlModal(true)}
                                                        className="flex items-center gap-1 bg-[var(--surface-inset)] border border-[var(--border)] hover:bg-[var(--surface-raised)] px-3 py-1.5 rounded-lg transition text-xs"
                                                    >
                                                        <Plus className="w-3.5 h-3.5" />
                                                        {tx.addUrl}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Data Overview */}
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                                <div className="bg-[var(--surface-raised)] rounded-lg p-3 text-center">
                                                    <div className="text-xl font-bold">{(selectedAerodrome.runways || []).length}</div>
                                                    <div className="text-[10px] text-[var(--text-secondary)] uppercase">{tx.runways}</div>
                                                </div>
                                                <div className="bg-[var(--surface-raised)] rounded-lg p-3 text-center">
                                                    <div className="text-xl font-bold">{(selectedAerodrome.taxiways || []).length}</div>
                                                    <div className="text-[10px] text-[var(--text-secondary)] uppercase">{tx.taxiways}</div>
                                                </div>
                                                <div className="bg-[var(--surface-raised)] rounded-lg p-3 text-center">
                                                    <div className="text-xl font-bold">{(selectedAerodrome.stands || []).length}</div>
                                                    <div className="text-[10px] text-[var(--text-secondary)] uppercase">{tx.stands}</div>
                                                </div>
                                                <div className="bg-[var(--surface-raised)] rounded-lg p-3 text-center">
                                                    <div className="text-xl font-bold">{(selectedAerodrome.frequencies || []).length}</div>
                                                    <div className="text-[10px] text-[var(--text-secondary)] uppercase">{tx.frequencies}</div>
                                                </div>
                                            </div>

                                            {/* Runways Table */}
                                            {(selectedAerodrome.runways || []).length > 0 && (
                                                <div className="mb-4">
                                                    <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-2">{tx.runways}</h4>
                                                    <div className="bg-[var(--surface-raised)] rounded-lg overflow-hidden">
                                                        <table className="w-full text-xs">
                                                            <thead><tr className="text-[var(--text-tertiary)] border-b border-[var(--border-subtle)]">
                                                                <th className="text-left px-3 py-2">RWY</th>
                                                                <th className="text-right px-3 py-2">Length</th>
                                                                <th className="text-right px-3 py-2">Width</th>
                                                                <th className="text-left px-3 py-2">Surface</th>
                                                                <th className="text-center px-3 py-2">ILS</th>
                                                                <th className="text-left px-3 py-2">PCN</th>
                                                            </tr></thead>
                                                            <tbody className="divide-y divide-[var(--border-subtle)]">
                                                                {(selectedAerodrome.runways as any[]).map((rwy, i) => (
                                                                    <tr key={i} className="text-[var(--text-primary)]">
                                                                        <td className="px-3 py-1.5 font-mono font-bold">{rwy.designator || `${rwy.designator_a}/${rwy.designator_b}`}</td>
                                                                        <td className="px-3 py-1.5 text-right">{rwy.length_m}m</td>
                                                                        <td className="px-3 py-1.5 text-right">{rwy.width_m}m</td>
                                                                        <td className="px-3 py-1.5">{rwy.surface || '—'}</td>
                                                                        <td className="px-3 py-1.5 text-center">{rwy.ils_category || (rwy.ils_a ? '✅' : '—')}</td>
                                                                        <td className="px-3 py-1.5">{rwy.pcn || '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* AIP Source URLs */}
                                            {aipSources.length > 0 && (
                                                <div>
                                                    <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-2">{tx.sourceUrls}</h4>
                                                    <div className="space-y-2">
                                                        {aipSources.map((src) => (
                                                            <div key={src.id} className="bg-[var(--surface-raised)] rounded-lg p-3 flex items-center gap-3">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-xs font-mono truncate text-[var(--text-primary)]">{src.url}</div>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <span className="text-[10px] text-[var(--text-tertiary)]">{src.source_type}</span>
                                                                        {src.portal_name && <span className="text-[10px] text-[var(--text-tertiary)]">· {src.portal_name}</span>}
                                                                        <span className={`text-[10px] px-1 rounded ${
                                                                            src.last_fetch_status === 'success' ? 'text-green-400' :
                                                                            src.last_fetch_status === 'failed' ? 'text-red-400' : 'text-yellow-400'
                                                                        }`}>{src.last_fetch_status}</span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={async () => {
                                                                        try {
                                                                            await aerodromeService.deleteSource(selectedAerodrome.icao_code, src.id);
                                                                            setAipSources(prev => prev.filter(s => s.id !== src.id));
                                                                        } catch {}
                                                                    }}
                                                                    className="p-1.5 hover:bg-[var(--surface-inset)] rounded-lg transition"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Metadata */}
                                            <div className="mt-4 pt-3 border-t border-[var(--border-subtle)] grid grid-cols-2 gap-2 text-[10px] text-[var(--text-tertiary)]">
                                                <div>{tx.dataSource}: <span className="text-[var(--text-secondary)]">{selectedAerodrome.data_source}</span></div>
                                                <div>{tx.confidence}: <span className="text-[var(--text-secondary)]">{Math.round(selectedAerodrome.confidence * 100)}%</span></div>
                                                <div>Seed: <span className="text-[var(--text-secondary)]">{selectedAerodrome.last_seed_at ? new Date(selectedAerodrome.last_seed_at).toLocaleDateString() : '—'}</span></div>
                                                <div>{tx.lastEnriched}: <span className="text-[var(--text-secondary)]">{selectedAerodrome.last_enriched_at ? new Date(selectedAerodrome.last_enriched_at).toLocaleDateString() : '—'}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] flex items-center justify-center p-12">
                                        <div className="text-center">
                                            <Database className="w-10 h-10 text-[var(--text-tertiary)] mx-auto mb-3" />
                                            <p className="text-[var(--text-secondary)] text-sm">{tx.selectAerodrome}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Register AIP URL Modal */}
            {showUrlModal && selectedAerodrome && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-[var(--surface)] rounded-2xl border border-[var(--border)] p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">{tx.registerUrl}</h2>
                        <p className="text-sm text-[var(--text-secondary)] mb-4">{selectedAerodrome.icao_code} — {selectedAerodrome.name}</p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.url}</label>
                                <input
                                    type="url"
                                    value={newUrl.url}
                                    onChange={(e) => setNewUrl({ ...newUrl, url: e.target.value })}
                                    placeholder="https://..."
                                    className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none text-sm font-mono"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.sourceType}</label>
                                    <select
                                        value={newUrl.source_type}
                                        onChange={(e) => setNewUrl({ ...newUrl, source_type: e.target.value })}
                                        className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-3 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none text-sm"
                                    >
                                        <option value="pdf_url">PDF URL</option>
                                        <option value="html_url">HTML URL</option>
                                        <option value="portal_url">Portal URL</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.portal}</label>
                                    <input
                                        type="text"
                                        value={newUrl.portal_name}
                                        onChange={(e) => setNewUrl({ ...newUrl, portal_name: e.target.value })}
                                        placeholder="ENAIRE, NATS..."
                                        className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-3 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={() => { setShowUrlModal(false); setNewUrl({ url: '', source_type: 'pdf_url', portal_name: '', notes: '' }); }}
                                    className="flex-1 bg-[var(--surface-raised)] hover:bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg py-2 transition"
                                >
                                    {tx.cancel}
                                </button>
                                <button
                                    onClick={async () => {
                                        if (!newUrl.url) return;
                                        try {
                                            await aerodromeService.registerSource(selectedAerodrome.icao_code, {
                                                url: newUrl.url,
                                                source_type: newUrl.source_type,
                                                portal_name: newUrl.portal_name || undefined,
                                                notes: newUrl.notes || undefined,
                                            });
                                            const sources = await aerodromeService.listSources(selectedAerodrome.icao_code);
                                            setAipSources(sources);
                                            setShowUrlModal(false);
                                            setNewUrl({ url: '', source_type: 'pdf_url', portal_name: '', notes: '' });
                                            setAipMessage({ type: 'success', text: 'URL registered' });
                                        } catch (err: any) {
                                            setAipMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to register URL' });
                                        }
                                    }}
                                    disabled={!newUrl.url}
                                    className="flex-1 bg-gradient-to-r from-[#F26430] to-orange-500 text-white rounded-lg py-2 font-medium hover:opacity-90 transition disabled:opacity-50"
                                >
                                    {tx.addUrl}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            {showCreateModal && (
                <CreateUserModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => { setShowCreateModal(false); loadData(); }}
                />
            )}
        </div>
    );
};

// Create User Modal Component
const CreateUserModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
    const { language } = useLanguage();
    const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;
    const [formData, setFormData] = useState({
        email: '',
        full_name: '',
        role: 'viewer',
        is_active: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await api.post('/admin/users', formData);
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error creating user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-[var(--surface)] rounded-2xl border border-[var(--border)] p-6 w-full max-w-md">
                <h2 className="text-xl font-bold mb-6">{tx.createUser}</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.fullName}</label>
                        <input
                            type="text"
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.email}</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-[var(--text-secondary)] mb-1">{tx.role}</label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="w-full bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none"
                        >
                            <option value="viewer">Viewer</option>
                            <option value="client_admin">Client Admin</option>
                            <option value="superadmin">Super Admin</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="rounded"
                        />
                        <label htmlFor="is_active" className="text-sm">{tx.activeOnCreation}</label>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-red-400 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 bg-[var(--surface-raised)] hover:bg-[var(--surface-inset)] border border-[var(--border)] rounded-lg py-2 transition"
                        >
                            {tx.cancel}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-gradient-to-r from-[#F26430] to-orange-500 text-white rounded-lg py-2 font-medium hover:opacity-90 transition disabled:opacity-50"
                        >
                            {loading ? tx.creating : tx.create}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminPage;
